import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import ThirdPane from '../ThirdPane';
import ThirdPaneContent from '../ThirdPane/ThirdPaneContent';
import ThirdPaneHeader, {
  ThirdPaneHeaderProps,
} from '../ThirdPane/ThirdPaneHeader';
import WaterSupplierDetail, { WaterSupplier } from './WaterSupplierDetail';
import Button from '../Button/Button';
import { PortalWithState } from 'react-portal';
import WaterSupplierEditModal from './WaterSupplierEditModal';
import { gql } from '@/modes/admin/gql';

export const GET_WATER_SUPPLIER = gql(`
  query GetWaterSupplier($where: JSON) {
    waterSuppliers(where: $where) {
      profileId
      user {
        email
      }
      profile {
        id
        type
        name
        phones {
          id
          subType
          value
          extension
        }
        emails {
          id
          subType
          value
        }
        contactAddressId
        contactAddress {
          street1
          street2
          city
          state
          zip
          zipExt
        }
      }
      acceptEmail
      reportName
      gracePeriod
      rpCheck2Type
    }
  }
`);

type BackflowTestThirdPaneProps = Partial<ThirdPaneHeaderProps> & {
  profileId?: string;
  reload?: () => void;
};

export default function WaterSupplierThirdPane({
  profileId,
  reload,
  onPrev,
  onNext,
  onToggle,
  prevEnabled,
  nextEnabled,
}: BackflowTestThirdPaneProps): JSX.Element {
  const {
    loading: itemLoading,
    error: itemError,
    data: itemData,
    refetch,
  } = useQuery<{
    waterSuppliers: any[];
  }>(GET_WATER_SUPPLIER, {
    skip: !profileId,
    variables: { where: { profileId } },
  });

  const [waterSupplier, setWaterSupplier] = useState<WaterSupplier>();

  useEffect(() => {
    if (itemData?.waterSuppliers?.length) {
      setWaterSupplier(itemData?.waterSuppliers[0]);
    }
  }, [profileId, itemData, setWaterSupplier]);

  if (itemError) {
    throw itemError;
  }

  return (
    <ThirdPane visible={!!profileId}>
      <ThirdPaneHeader
        onToggle={onToggle}
        title={waterSupplier?.profileId ?? 'Loading...'}
        onPrev={onPrev}
        onNext={onNext}
        prevEnabled={prevEnabled}
        nextEnabled={nextEnabled}
      >
        <PortalWithState closeOnEsc>
          {({ openPortal, closePortal, portal }) => (
            <>
              <Button
                mr={10}
                icon="edit"
                variant="outline"
                onClick={openPortal}
              >
                Edit
              </Button>
              {portal(
                <WaterSupplierEditModal
                  action="edit"
                  onClose={() => {
                    closePortal();
                    refetch();
                  }}
                  waterSupplier={waterSupplier}
                />
              )}
            </>
          )}
        </PortalWithState>
      </ThirdPaneHeader>
      <ThirdPaneContent>
        {waterSupplier && <WaterSupplierDetail waterSupplier={waterSupplier} />}
      </ThirdPaneContent>
    </ThirdPane>
  );
}
